// Editor-gated correction modal for SOW originalValue typo repair. The
// standard PUT path rejects originalValue at the schema layer because
// value changes should flow through approved variations; this surface
// exists only for genuine data-entry mistakes. The required `reason`
// field plus the audit row recorded server-side is the control —
// admin role is not required.
//
// Form: a decimal-aware CurrencyInput for the new value (AUD with up to
// 2 decimal places, ex-GST per the stored schema) and a reason textarea
// (10–500 chars). Submit fires the useCorrectSowOriginalValue mutation;
// on 200 the modal closes and the parent re-fetches.

import { useState } from "react";
import Modal from "../../../common/Modal";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";
import CurrencyInput from "../shared/CurrencyInput";
import { gstSuffix } from "../../../../utils/gstSuffix";
import { ApiError } from "../../../../services/api";
import type { Sow } from "../../../../types";

const MIN_REASON = 10;
const MAX_REASON = 500;

interface CorrectSowOriginalValueModalProps {
  isOpen: boolean;
  onClose: () => void;
  sow: Sow | null;
  gstApplies: boolean | undefined;
  onConfirm: (newOriginalValue: number, reason: string) => Promise<unknown>;
  isSubmitting?: boolean;
}

export default function CorrectSowOriginalValueModal({
  isOpen,
  onClose,
  sow,
  gstApplies,
  onConfirm,
  isSubmitting,
}: CorrectSowOriginalValueModalProps) {
  const [valueRaw, setValueRaw] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Pattern 0: seed fields from the target SOW when the modal opens so a
  // previous correction attempt does not persist across invocations.
  const seedKey = isOpen && sow ? sow.id : null;
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (seedKey !== null && seedKey !== seededFor) {
    setSeededFor(seedKey);
    setValueRaw(String(sow!.originalValue));
    setReason("");
    setError(null);
  }
  if (!isOpen && seededFor !== null) {
    setSeededFor(null);
  }

  // Round to 2dp on the client so a copy-paste value like 19.999 collapses
  // to 20.00 before reaching the backend monetaryAmount schema.
  const parsedRaw = valueRaw === "" || valueRaw === "." ? null : Number(valueRaw);
  const parsed =
    parsedRaw !== null && Number.isFinite(parsedRaw)
      ? Math.round(parsedRaw * 100) / 100
      : null;
  const valueValid = parsed !== null;
  const valueChanged = sow !== null && valueValid && parsed !== sow.originalValue;
  const reasonValid =
    reason.trim().length >= MIN_REASON && reason.length <= MAX_REASON;
  const canSubmit =
    !isSubmitting && sow !== null && valueValid && valueChanged && reasonValid;

  const basis = gstSuffix(gstApplies);

  const handleConfirm = async () => {
    if (!canSubmit || parsed === null) return;
    setError(null);
    try {
      await onConfirm(parsed, reason.trim());
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        return;
      }
      setError(
        err instanceof Error ? err.message : "Failed to correct value.",
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        sow ? `Correct value of ${sow.number}?` : "Correct SOW value"
      }
    >
      <div className="space-y-4" data-testid="correct-sow-original-value-modal">
        <Alert variant="warning">
          This action is logged to the audit trail. If the original value
          was correct and a change is needed, raise a variation instead.
        </Alert>
        {sow && (
          <dl className="grid grid-cols-2 gap-3 rounded-md bg-slate-50 dark:bg-slate-800/60 p-3">
            <div>
              <dt className="text-caption text-slate-500 dark:text-slate-400">
                SOW
              </dt>
              <dd className="text-body text-slate-900 dark:text-slate-100">
                {sow.name}
              </dd>
            </div>
            <div>
              <dt className="text-caption text-slate-500 dark:text-slate-400">
                Current value
              </dt>
              <dd className="text-body tabular-nums text-slate-900 dark:text-slate-100">
                $
                {sow.originalValue.toLocaleString("en-AU", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{" "}
                <span className="text-caption text-slate-500 dark:text-slate-400">
                  {basis}
                </span>
              </dd>
            </div>
          </dl>
        )}
        <div>
          <label
            htmlFor="correct-sow-original-value-input"
            className="label"
          >
            New value
          </label>
          <div className="relative w-[220px]">
            <span
              aria-hidden="true"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400"
            >
              $
            </span>
            <CurrencyInput
              id="correct-sow-original-value-input"
              value={valueRaw}
              onChange={setValueRaw}
              allowDecimal
              ariaLabel="New SOW value"
              disabled={isSubmitting}
              placeholder="0"
              testId="correct-sow-original-value-input"
            />
          </div>
          <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
            Up to 2 decimal places, {basis}.
          </p>
        </div>
        <div>
          <label htmlFor="correct-sow-original-value-reason" className="label">
            Reason
          </label>
          <textarea
            id="correct-sow-original-value-reason"
            data-testid="correct-sow-original-value-reason"
            className="input min-h-[80px]"
            value={reason}
            maxLength={MAX_REASON}
            onChange={(e) => setReason(e.target.value)}
            disabled={isSubmitting}
            required
          />
          <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
            {reason.length}/{MAX_REASON} (minimum {MIN_REASON})
          </p>
        </div>
        {error && <Alert variant="error">{error}</Alert>}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            loading={isSubmitting}
            disabled={!canSubmit}
            data-testid="correct-sow-original-value-submit"
          >
            Save correction
          </Button>
        </div>
      </div>
    </Modal>
  );
}
